"use client";

import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { X, Minus, Plus, Trash2 } from "lucide-react";

export function CartDrawer() {
  const {
    isOpen,
    setIsOpen,
    items,
    totalQuantity,
    cost,
    checkoutUrl,
    loading,
    updateQuantity,
    removeItem,
  } = useCart();


  // State to track input values during editing
  const [quantityInputs, setQuantityInputs] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const formatPrice = (amount: string | number, currencyCode: string | undefined) => {
    if (!amount || !currencyCode) return "";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currencyCode,
    }).format(Number(amount));
  };

  const handleQuantityChange = async (
    lineId: string,
    currentQuantity: number,
    delta: number
  ) => {
    const newQuantity = Math.max(0, currentQuantity + delta);
    if (newQuantity === 0) {
      await removeItem(lineId);
    } else {
      await updateQuantity(lineId, newQuantity);
      // Update the input state to match the new quantity
      setQuantityInputs({
        ...quantityInputs,
        [lineId]: newQuantity.toString()
      });
    }
  };

  const handleRemoveItem = async (lineId: string) => {
    await removeItem(lineId);
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={() => setIsOpen(false)}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">
            Shopping Cart ({totalQuantity})
          </h2>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto max-h-[calc(100vh-200px)] p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Your cart is empty</p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg"
                >
                  {/* Product Image */}
                  <div className="w-20 h-20 flex-shrink-0 bg-white rounded border overflow-hidden">
                    {item?.merchandise?.image ? (
                      <img
                        src={item.merchandise.image.url}
                        alt={
                          item.merchandise.image.altText ||
                          item?.merchandise?.product?.title
                        }
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <span className="text-gray-400 text-sm">No image</span>
                      </div>
                    )}
                  </div>

                  {/* Product Details */}
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <h3 className="font-medium">
                        {item?.merchandise?.product?.title}
                      </h3>
                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-gray-500 text-sm mt-1">
                      {item?.merchandise?.title}
                    </p>
                    <div className="flex justify-between items-center mt-2">
                      <div className="flex items-center border rounded-md">
                        <button
                          onClick={() =>
                            handleQuantityChange(item.id, item.quantity, -1)
                          }
                          className="p-1 hover:bg-gray-100"
                          disabled={loading}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <input
                          type="text"
                          value={quantityInputs[item.id] !== undefined ? quantityInputs[item.id] : item.quantity}
                          onChange={(e) => {
                            // Only update local state during typing
                            setQuantityInputs({
                              ...quantityInputs,
                              [item.id]: e.target.value
                            });
                          }}
                          onBlur={(e) => {
                            // Update cart when user finishes editing
                            const newValue = parseInt(e.target.value);
                            if (!isNaN(newValue) && newValue >= 1) {
                              updateQuantity(item.id, newValue);
                            } else {
                              // Reset to current quantity if invalid
                              setQuantityInputs({
                                ...quantityInputs,
                                [item.id]: item.quantity.toString()
                              });
                            }
                          }}
                          onKeyDown={(e) => {
                            // Update cart when user presses Enter
                            if (e.key === 'Enter') {
                              e.currentTarget.blur();
                            }
                          }}
                          className="w-12 text-center px-2 py-1 text-gray-900 border-0 focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        <button
                          onClick={() =>
                            handleQuantityChange(item.id, item.quantity, 1)
                          }
                          className="p-1 hover:bg-gray-100"
                          disabled={loading}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-gray-900 font-medium">
                        {formatPrice(
                          item?.cost?.totalAmount?.amount,
                          item?.cost?.totalAmount?.currencyCode
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && cost && (
          <div className="border-t p-4 space-y-4 bg-white">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span>
                  {formatPrice(
                    cost?.subtotalAmount?.amount,
                    cost?.subtotalAmount?.currencyCode
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between font-semibold">
                <span>Total</span>
                <span>
                  {formatPrice(
                    cost?.totalAmount?.amount,
                    cost?.totalAmount?.currencyCode
                  )}
                </span>
              </div>
            </div>
            <a
              href={checkoutUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-primary  text-white text-center py-3 rounded-md font-medium hover:bg-secondary  transition-colors"
            >
              Proceed to Checkout
            </a>
          </div>
        )}
      </div>
    </>
  );
}
